import { Media } from '../Model/Media/Media';
import { CoachTts, CoachTtsApi } from '../Model/Coach/TextToSpeech/CoachTts';
import { ViewSet } from '../Model/WorkoutLog/ViewSet';
import { translate } from './System/LocalizationStore';
import Qty from 'js-quantities';

/**
 * returns a randomly selected quote from the list passed
 * @param quotesIdentifier
 */
function randomQuote(quotesIdentifier: string[]): string | undefined {
  return quotesIdentifier[Math.floor(Math.random() * quotesIdentifier.length)];
}

export function loadAudio(
  quoteIdentifier: string,
  values: Record<string, string> = {},
  language: string = 'de',
): Promise<Media | undefined> {
  return CoachTtsApi.generateSingle(quoteIdentifier, { language, values });
}

export function loadRandomAudio(
  quoteIdentifiers: string[],
  values: Record<string, string> = {},
  language: string = 'de',
): Promise<Media | undefined> {
  const quoteId = randomQuote(quoteIdentifiers);
  if (quoteId) {
    return loadAudio(quoteId, values, language);
  }
  return Promise.resolve(undefined);
}

export function resolveTtsParamsDuration(viewSet: ViewSet, language: string): Record<string, string> | undefined {
  const { plannedDuration } = viewSet;
  const exerciseName = viewSet.exercise.localeName(language);

  if (plannedDuration) {
    if (plannedDuration > 60) {
      const minutes = Math.floor(plannedDuration / 60);
      const seconds = Math.floor(60 * (plannedDuration / 60 - minutes));
      const minuteUnit =
        minutes > 1
          ? translate('globals.time.minutes', { defaultValue: 'minutes' })
          : translate('globals.time.minute', { defaultValue: 'minute' });
      const secondsUnit =
        seconds > 1
          ? translate('globals.time.seconds', { defaultValue: 'seconds' })
          : translate('globals.time.second', { defaultValue: 'second' });

      if (seconds > 0) {
        return {
          exerciseName,
          value: `${minutes} ${minuteUnit} ${translate('globals.and', {
            defaultValue: 'and',
          })} ${seconds} ${secondsUnit}`,
        };
      }

      return {
        exerciseName,
        value: `${minutes} ${minuteUnit}`,
      };
    }

    return {
      exerciseName,
      value: `${plannedDuration} ${translate('globals.time.seconds', { defaultValue: 'seconds' })}`,
    };
  }
}

function resolveTtsParamsReps(
  viewSet: ViewSet,
  language: string,
  metric: boolean = true,
): Record<string, string> | undefined {
  const { plannedRepetitions, plannedWeight } = viewSet;
  const exerciseName = viewSet.exercise.localeName(language);
  const value = `${plannedRepetitions} ${translate('trackingKeys.REPETITIONS', { defaultValue: 'repetitions' })}`;

  if (plannedRepetitions) {
    if (plannedWeight) {
      const unit = metric
        ? translate('tts.kg', { defaultValue: 'kilos' })
        : translate('tts.lbs', { defaultValue: 'pounds' });
      const weight = metric
        ? Math.round(plannedWeight * 100) / 100
        : Math.round(new Qty(plannedWeight, 'kg').to('lbs').scalar * 4) / 4;

      return {
        exerciseName,
        value: `${value} ${translate('globals.with', { defaultValue: 'with' })} ${weight} ${unit}`,
      };
    } else if (viewSet.assignedTrackingKeys.includes('WEIGHT')) {
      return {
        exerciseName,
        value: `${value}, ${translate('tts.chooseWeightThatMatchesReps', {
          defaultValue: 'choose a suitable weight for you and repetitions',
        })}`,
      };
    }

    return {
      exerciseName,
      value,
    };
  }
}

function resolveTtsParams(
  viewSet: ViewSet,
  language: string,
  metric: boolean = true,
): Record<string, string> | undefined {
  const durationBased = viewSet.durationBased || viewSet.viewBlock.viewPhase.viewWorkout.autoPlayEnabled;

  if (durationBased) {
    return resolveTtsParamsDuration(viewSet, language);
  }

  return resolveTtsParamsReps(viewSet, language, metric);
}

export function loadNextExerciseOrSetAudio(
  nextSet: ViewSet,
  language: string = 'de',
  metric: boolean = true,
): Promise<Media | undefined> {
  if (nextSet) {
    if (nextSet.index === 0) {
      const params = resolveTtsParams(nextSet, language, metric);
      if (params) {
        if (nextSet.exercise.isOneSided) {
          return loadAudio(
            'nextExerciseOneSided_2',
            Object.assign(params, {
              side: translate('tts.leftSide', { defaultValue: 'left side' }),
            }),
            language,
          );
        }
        return loadRandomAudio(
          ['nextExerciseRepsOrDuration_1', 'nextExerciseRepsOrDuration_2', 'nextExerciseRepsOrDuration_3'],
          params,
          language,
        );
      }
      return Promise.resolve(undefined);
    }

    if (nextSet.exercise.isOneSided) {
      return loadRandomAudio(['nextSetChangeSide_1'], {}, language);
    }

    return loadRandomAudio(['nextSet_1'], {}, language);
  }

  return Promise.resolve(undefined);
}
